import { useMutation, useQuery } from "@apollo/client/react";
import { useMarketingWorkspaceConfig } from "../config";
import {
  CREATE_MARKETING_WORKSPACE,
  DELETE_MARKETING_WORKSPACE,
  GET_MY_MARKETING_WORKSPACES,
  UPDATE_MARKETING_WORKSPACE,
} from "../graphql/workspaceDocuments";
import type {
  ICreateMarketingWorkspaceInput,
  IMarketingWorkspace,
  MarketingWorkspacePlan,
} from "../types";

export function useMarketingWorkspaces(skip = false) {
  const { getClient } = useMarketingWorkspaceConfig();

  const { data, loading, error, refetch } = useQuery<{ myMarketingWorkspaces: IMarketingWorkspace[] }>(GET_MY_MARKETING_WORKSPACES, {
    client: getClient(),
    skip,
    fetchPolicy: "cache-and-network",
  });

  return {
    workspaces: (data?.myMarketingWorkspaces ?? []) as IMarketingWorkspace[],
    hasLoaded: Boolean(data),
    isLoading: loading,
    error,
    refetch,
  };
}

export function useCreateMarketingWorkspace() {
  const { getClient } = useMarketingWorkspaceConfig();

  const [mutate, { loading, error }] = useMutation<{ createMarketingWorkspace: IMarketingWorkspace }>(CREATE_MARKETING_WORKSPACE, {
    client: getClient(),
    refetchQueries: [GET_MY_MARKETING_WORKSPACES],
    awaitRefetchQueries: true,
  });

  const createWorkspace = async (input: ICreateMarketingWorkspaceInput) => {
    const result = await mutate({ variables: { request: input } });
    return result.data?.createMarketingWorkspace as IMarketingWorkspace;
  };

  return { createWorkspace, isLoading: loading, error };
}

export function useUpdateMarketingWorkspace() {
  const { getClient } = useMarketingWorkspaceConfig();

  const [mutate, { loading, error }] = useMutation<{ updateMarketingWorkspace: IMarketingWorkspace }>(UPDATE_MARKETING_WORKSPACE, {
    client: getClient(),
    refetchQueries: [GET_MY_MARKETING_WORKSPACES],
    awaitRefetchQueries: true,
  });

  const updateWorkspace = async (
    id: string,
    input: { name?: string; plan?: MarketingWorkspacePlan }
  ) => {
    const result = await mutate({ variables: { id, request: input } });
    return result.data?.updateMarketingWorkspace as IMarketingWorkspace;
  };

  return { updateWorkspace, isLoading: loading, error };
}

export function useDeleteMarketingWorkspace() {
  const { getClient } = useMarketingWorkspaceConfig();

  const [mutate, { loading, error }] = useMutation<{ deleteMarketingWorkspace: boolean }>(DELETE_MARKETING_WORKSPACE, {
    client: getClient(),
    refetchQueries: [GET_MY_MARKETING_WORKSPACES],
    awaitRefetchQueries: true,
  });

  const deleteWorkspace = async (id: string) => {
    const result = await mutate({ variables: { id } });
    return Boolean(result.data?.deleteMarketingWorkspace);
  };

  return { deleteWorkspace, isLoading: loading, error };
}
